import { useState, useEffect, useMemo } from 'react';
import { sysadminService, IndicadorBaseSysadmin } from '@/services/sysadmin.service';
import { Card, Button, Input, Select, Modal, LoadingContainer, Badge } from '@/components/ui';
import { Search, Edit2, BarChart3 } from 'lucide-react';

const FRECUENCIA_OPTIONS = [
  { value: 'MENSUAL', label: 'Mensual' },
  { value: 'TRIMESTRAL', label: 'Trimestral' },
  { value: 'SEMESTRAL', label: 'Semestral' },
  { value: 'ANUAL', label: 'Anual' },
];

const TIPO_RESULTADO_OPTIONS = [
  { value: 'PORCENTAJE', label: 'Porcentaje (%)' },
  { value: 'NUMERO', label: 'Número' },
  { value: 'MONEDA', label: 'Moneda' },
];

interface FormState {
  nombre: string;
  formula: string;
  frecuencia: 'MENSUAL' | 'TRIMESTRAL' | 'SEMESTRAL' | 'ANUAL';
  fuenteDatos: string;
  meta: string;
  tipoResultado: 'PORCENTAJE' | 'NUMERO' | 'MONEDA';
}

const initialForm: FormState = {
  nombre: '',
  formula: '',
  frecuencia: 'MENSUAL',
  fuenteDatos: '',
  meta: '',
  tipoResultado: 'PORCENTAJE',
};

export function GestionIndicadoresPage() {
  const [indicadores, setIndicadores] = useState<IndicadorBaseSysadmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedIndicador, setSelectedIndicador] = useState<IndicadorBaseSysadmin | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<FormState>(initialForm);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchIndicadores = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await sysadminService.listarIndicadoresBase();
      if (response.success) {
        setIndicadores(response.baseIndicadores || []);
      } else {
        setError('Error al obtener indicadores base');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al obtener indicadores base');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchIndicadores();
  }, []);

  const filteredIndicadores = useMemo(() => {
    if (!search) return indicadores;
    const q = search.toLowerCase();
    return indicadores.filter(
      (ind) => ind.nombre.toLowerCase().includes(q) || ind.formula.toLowerCase().includes(q) || ind.fuenteDatos.toLowerCase().includes(q)
    );
  }, [indicadores, search]);

  const openEdit = (indicador: IndicadorBaseSysadmin) => {
    setSelectedIndicador(indicador);
    setForm({
      nombre: indicador.nombre,
      formula: indicador.formula,
      frecuencia: indicador.frecuencia,
      fuenteDatos: indicador.fuenteDatos,
      meta: String(indicador.meta),
      tipoResultado: indicador.tipoResultado,
    });
    setActionError(null);
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedIndicador) return;
    if (!form.nombre || !form.formula || !form.fuenteDatos || form.meta === '') {
      setActionError('Todos los campos son obligatorios');
      return;
    }

    const metaValue = Number(form.meta);
    if (Number.isNaN(metaValue)) {
      setActionError('La meta debe ser un número válido');
      return;
    }

    setSaving(true);
    setActionError(null);

    try {
      const response = await sysadminService.actualizarIndicadorBase(selectedIndicador.id, {
        nombre: form.nombre.trim(),
        formula: form.formula.trim(),
        frecuencia: form.frecuencia,
        fuenteDatos: form.fuenteDatos.trim(),
        meta: metaValue,
        tipoResultado: form.tipoResultado,
      });

      if (response.success) {
        setShowModal(false);
        fetchIndicadores();
      } else {
        setActionError('Error al actualizar el indicador');
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return <LoadingContainer text="Cargando indicadores base..." />;
  if (error) return <p className="text-red-600 text-center py-8">{error}</p>;

  return (
    <div className="space-y-4">
      {/* Barra superior */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Buscar por nombre, fórmula..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
      </div>

      {/* Tabla */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: '800px' }}>
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Indicador</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fórmula</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Frecuencia</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fuente</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Meta</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Tipo Resultado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Variables</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredIndicadores.map((ind) => (
                <tr key={ind.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4.5 h-4.5 text-gray-400 shrink-0" />
                      <span className="font-medium text-gray-900">{ind.nombre}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs">{ind.formula}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant="blue">{ind.frecuencia}</Badge>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{ind.fuenteDatos}</td>
                  <td className="px-4 py-3 text-center font-semibold text-gray-800">
                    {ind.tipoResultado === 'PORCENTAJE'
                      ? `${ind.meta}%`
                      : ind.tipoResultado === 'MONEDA'
                        ? `$${ind.meta.toLocaleString('es-CO')}`
                        : ind.meta}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-600 text-xs">{ind.tipoResultado}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {ind.variablesBase && ind.variablesBase.length > 0 ? (
                        ind.variablesBase.map((v) => (
                          <span key={v.nombre} className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">
                            {v.nombre}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400 italic">Ninguna</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(ind)} title="Editar indicador">
                      <Edit2 className="w-4 h-4 text-gray-500 hover:text-gray-800" />
                    </Button>
                  </td>
                </tr>
              ))}
              {filteredIndicadores.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    No se encontraron indicadores de calidad.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal de edición */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Editar Indicador de Calidad" size="md">
        <form onSubmit={handleSave} className="space-y-4">
          <Input
            label="Nombre del Indicador"
            value={form.nombre}
            onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))}
            required
            placeholder="Ej. Cumplimiento de metas"
          />

          <Input
            label="Fórmula"
            value={form.formula}
            onChange={(e) => setForm((prev) => ({ ...prev, formula: e.target.value }))}
            required
            placeholder="Ej. (A / B) * 100"
          />

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Frecuencia"
              value={form.frecuencia}
              onChange={(e) => setForm((prev) => ({ ...prev, frecuencia: e.target.value as any }))}
              options={FRECUENCIA_OPTIONS}
            />

            <Select
              label="Tipo de Resultado"
              value={form.tipoResultado}
              onChange={(e) => setForm((prev) => ({ ...prev, tipoResultado: e.target.value as any }))}
              options={TIPO_RESULTADO_OPTIONS}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Meta"
              type="number"
              step="any"
              value={form.meta}
              onChange={(e) => setForm((prev) => ({ ...prev, meta: e.target.value }))}
              required
              placeholder="Ej. 80"
            />

            <Input
              label="Fuente de Datos"
              value={form.fuenteDatos}
              onChange={(e) => setForm((prev) => ({ ...prev, fuenteDatos: e.target.value }))}
              required
              placeholder="Ej. Auditorías internas"
            />
          </div>

          {actionError && <p className="text-[12px] text-red-600">{actionError}</p>}

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <Button type="button" variant="outline" onClick={() => setShowModal(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
